import React, { useState, useEffect, useCallback, FunctionComponent } from 'react';
import styled from 'styled-components';
import CoffeeChatQuestionFilter from './CoffeeChatQuestionFilter';
import CoffeeChatQuestionTable from './CoffeeChatQuestionTable';
import {
  CloseButton,
  FormGroup,
  FormLabel,
  HelperText,
  ModalTitle,
  PrimaryButton,
  SelectField,
  SidePanelBody,
  SidePanelHeader,
  SidePanelModal,
  SidePanelOverlay,
} from '../common/AdminModalPrimitives';
import AdminDateFilterModal from '../common/AdminDateFilterModal';
import {
  adminCoffeeChatApi,
  CoffeeChatQuestionData,
  CoffeeChatQuestionListResponse,
  CoffeeChatFilterState,
  CoffeeChatPaginationState,
} from '../../../services/admin/adminCoffeeChatApi';
import { adminCoffeeChatAnswerApi } from '../../../services/admin/adminCoffeeChatAnswerApi';
import { getMentorList } from '../../../services/admin/homeApi';
import { useUserInfo } from '../../../hooks/useUserInfo';
import RichTextEditor from '../../common/RichTextEditor';

const CoffeeChatQuestionManagement: FunctionComponent = () => {
  const { userInfo } = useUserInfo();

  const [questions, setQuestions] = useState<CoffeeChatQuestionData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showReplyModal, setShowReplyModal] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<CoffeeChatQuestionData | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [selectedMentorId, setSelectedMentorId] = useState<number | null>(null);
  const [mentors, setMentors] = useState<any[]>([]);
  const [loadingMentors, setLoadingMentors] = useState(false);
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);

  const [filter, setFilter] = useState<CoffeeChatFilterState>({
    mentorId: null,
    fromDate: '',
    toDate: '',
    visible: null,
    isAnswered: null,
    searchTerm: '',
  });

  const [showDateModal, setShowDateModal] = useState(false);
  const [tempFromDate, setTempFromDate] = useState('');
  const [tempToDate, setTempToDate] = useState('');

  const [pagination, setPagination] = useState<CoffeeChatPaginationState>({
    page: 0,
    size: 10,
    totalPages: 0,
    totalElements: 0,
  });

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response: CoffeeChatQuestionListResponse = await adminCoffeeChatApi.getCoffeeChatQuestions(filter, {
        page: pagination.page,
        size: pagination.size,
      });

      setQuestions(response.data.content);
      setPagination((prev) => ({
        ...prev,
        totalPages: response.data.totalPages,
        totalElements: response.data.totalElements,
      }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '알 수 없는 오류가 발생했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filter, pagination.page, pagination.size]);

  useEffect(() => {
    const fetchMentors = async () => {
      try {
        setLoadingMentors(true);
        const response = await getMentorList(0, 100, true);
        setMentors(response.content || []);
      } catch {
        setMentors([]);
      } finally {
        setLoadingMentors(false);
      }
    };

    fetchMentors();
  }, []);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  const isValidDateFormat = (dateString: string): boolean => {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateString)) {
      return false;
    }

    const date = new Date(dateString);
    return date instanceof Date && !Number.isNaN(date.getTime()) && dateString === date.toISOString().split('T')[0];
  };

  const handleDateModalOpen = () => {
    setTempFromDate(filter.fromDate);
    setTempToDate(filter.toDate);
    setShowDateModal(true);
  };

  const handleDateModalConfirm = () => {
    setFilter((prev) => ({
      ...prev,
      fromDate: tempFromDate,
      toDate: tempToDate,
    }));
    setPagination((prev) => ({ ...prev, page: 0 }));
    setShowDateModal(false);
  };

  const handleFilterChange = (newFilter: Partial<CoffeeChatFilterState>) => {
    if (newFilter.fromDate !== undefined || newFilter.toDate !== undefined) {
      const fromDate = newFilter.fromDate !== undefined ? newFilter.fromDate : filter.fromDate;
      const toDate = newFilter.toDate !== undefined ? newFilter.toDate : filter.toDate;

      const isFromDateValid = !fromDate || isValidDateFormat(fromDate);
      const isToDateValid = !toDate || isValidDateFormat(toDate);

      if (isFromDateValid && isToDateValid) {
        if (fromDate && toDate && fromDate > toDate) {
          if (newFilter.fromDate !== undefined) {
            newFilter.toDate = '';
          } else {
            newFilter.fromDate = '';
          }
        }

        setFilter((prev) => ({ ...prev, ...newFilter }));
        setPagination((prev) => ({ ...prev, page: 0 }));
        return;
      }
    }

    setFilter((prev) => ({ ...prev, ...newFilter }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleDeleteQuestion = async (questionId: number) => {
    const question = questions.find((item) => item.id === questionId);

    if (!question) {
      alert('삭제할 질문을 찾을 수 없습니다.');
      return;
    }

    const confirmMessage = `다음 질문을 삭제하시겠습니까?\n\n질문 ID: ${question.id}\n제목: ${question.title}\n멘토: ${question.mentor_name}\n작성자: ${question.writer_name}\n작성일: ${new Date(question.created_at).toLocaleDateString('ko-KR')}\n답변 상태: ${question.is_answered ? '답변 완료' : '답변 대기'}\n\n삭제된 데이터는 복구할 수 없습니다.`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      await adminCoffeeChatApi.deleteQuestion(questionId);
      alert(`질문이 삭제되었습니다.\n\nID: ${question.id}\n제목: ${question.title}`);
      await fetchQuestions();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '질문 삭제 중 오류가 발생했습니다.';
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleManageReply = (questionId: number) => {
    const question = questions.find((item) => item.id === questionId);
    if (!question) {
      return;
    }

    setSelectedQuestion(question);
    setReplyContent(question.answer_content || '');

    const questionMentor = mentors.find((mentor) => mentor.name === question.mentor_name);
    if (questionMentor) {
      setSelectedMentorId(questionMentor.id);
    } else if (mentors.length > 0) {
      setSelectedMentorId(mentors[0].id);
    } else {
      setSelectedMentorId(null);
    }

    setShowReplyModal(true);
  };

  const handleCloseReplyModal = () => {
    setShowReplyModal(false);
    setSelectedQuestion(null);
    setReplyContent('');
    setSelectedMentorId(null);
  };

  const handleSubmitReply = async () => {
    if (!selectedQuestion || !replyContent.trim()) {
      alert('답변 내용을 입력해 주세요.');
      return;
    }

    if (!selectedMentorId) {
      alert('답변 멘토를 선택해 주세요.');
      return;
    }

    setIsSubmittingReply(true);
    try {
      const writerId = userInfo?.id;
      if (!writerId) {
        throw new Error('사용자 정보를 찾을 수 없습니다. 다시 로그인해 주세요.');
      }

      const payloadWriterId = selectedMentorId || writerId;
      const content = replyContent.trim();

      if (selectedQuestion.is_answered && selectedQuestion.answer_id) {
        await adminCoffeeChatAnswerApi.updateAnswer(selectedQuestion.id, selectedQuestion.answer_id, {
          content,
          writerId: payloadWriterId,
        });
        alert('답변이 수정되었습니다.');
      } else {
        await adminCoffeeChatAnswerApi.createAnswer(selectedQuestion.id, {
          content,
          writerId: payloadWriterId,
        });
        alert('답변이 등록되었습니다.');
      }

      handleCloseReplyModal();
      await fetchQuestions();
    } catch (err) {
      const message = err instanceof Error ? err.message : '답변 등록에 실패했습니다.';
      alert(message);
    } finally {
      setIsSubmittingReply(false);
    }
  };

  const handleToggleVisibility = async (questionId: number, currentVisible: boolean) => {
    const newVisible = !currentVisible;
    const action = newVisible ? '노출' : '숨김';

    if (!window.confirm(`이 질문을 ${action} 처리하시겠습니까?`)) {
      return;
    }

    try {
      setLoading(true);
      await adminCoffeeChatApi.toggleQuestionVisibility(questionId, newVisible);
      alert(`질문을 ${action} 처리했습니다.`);
      await fetchQuestions();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '질문 노출 상태 변경 중 오류가 발생했습니다.';
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAnswer = async (question: CoffeeChatQuestionData) => {
    if (!question.answer_id) {
      return;
    }

    if (!window.confirm('이 답변을 삭제하시겠습니까? 삭제된 데이터는 복구할 수 없습니다.')) {
      return;
    }

    try {
      setLoading(true);
      await adminCoffeeChatAnswerApi.deleteAnswer(question.id, question.answer_id);
      alert('답변이 삭제되었습니다.');
      await fetchQuestions();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '답변 삭제 중 오류가 발생했습니다.';
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <CoffeeChatQuestionFilter filter={filter} onFilterChange={handleFilterChange} onDateModalOpen={handleDateModalOpen} />

      <CoffeeChatQuestionTable
        questions={questions}
        loading={loading}
        error={error}
        pagination={pagination}
        onDelete={handleDeleteQuestion}
        onToggleVisibility={handleToggleVisibility}
        onManageReply={handleManageReply}
        onDeleteAnswer={handleDeleteAnswer}
        onRetry={fetchQuestions}
        onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
      />

      {showDateModal && (
        <AdminDateFilterModal
          title="등록일 선택"
          startDate={tempFromDate}
          endDate={tempToDate}
          onStartDateChange={setTempFromDate}
          onEndDateChange={setTempToDate}
          onConfirm={handleDateModalConfirm}
          onCancel={() => setShowDateModal(false)}
        />
      )}

      {showReplyModal && selectedQuestion && (
        <CoffeeChatReplyModal
          question={selectedQuestion}
          replyContent={replyContent}
          setReplyContent={setReplyContent}
          selectedMentorId={selectedMentorId}
          setSelectedMentorId={setSelectedMentorId}
          mentors={mentors}
          loadingMentors={loadingMentors}
          isSubmitting={isSubmittingReply}
          onSubmit={handleSubmitReply}
          onClose={handleCloseReplyModal}
        />
      )}
    </Container>
  );
};

interface CoffeeChatReplyModalProps {
  question: CoffeeChatQuestionData;
  replyContent: string;
  setReplyContent: (content: string) => void;
  selectedMentorId: number | null;
  setSelectedMentorId: (mentorId: number | null) => void;
  mentors: any[];
  loadingMentors: boolean;
  isSubmitting: boolean;
  onSubmit: () => void;
  onClose: () => void;
}

const CoffeeChatReplyModal: FunctionComponent<CoffeeChatReplyModalProps> = ({
  question,
  replyContent,
  setReplyContent,
  selectedMentorId,
  setSelectedMentorId,
  mentors,
  loadingMentors,
  isSubmitting,
  onSubmit,
  onClose,
}) => (
  <SidePanelOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
    <SidePanelModal>
      <SidePanelHeader>
        <ModalTitle>{question.is_answered ? '답변 수정' : '답변 등록'}</ModalTitle>
        <CloseButton type="button" onClick={onClose} aria-label="닫기">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15 5L5 15M5 5L15 15" stroke="#171719" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </CloseButton>
      </SidePanelHeader>

      <SidePanelBody>
        <InfoTopRow>
          <InfoRow>
            <InfoLabel>연번</InfoLabel>
            <InfoText>{question.id}</InfoText>
          </InfoRow>
          <InfoRow>
            <InfoLabel>작성자</InfoLabel>
            <InfoText>{question.writer_name}</InfoText>
          </InfoRow>
          <InfoRow>
            <InfoLabel>작성일</InfoLabel>
            <InfoText>{new Date(question.created_at).toLocaleDateString('ko-KR')}</InfoText>
          </InfoRow>
        </InfoTopRow>

        <InfoRow>
          <InfoLabel>제목</InfoLabel>
          <InfoText>{question.title}</InfoText>
        </InfoRow>

        <InfoRow>
          <InfoLabel>내용</InfoLabel>
          {question.content ? (
            <ContentView dangerouslySetInnerHTML={{ __html: question.content }} />
          ) : (
            <InfoText>-</InfoText>
          )}
        </InfoRow>

        <FormGroup>
          <FormLabel>답변 멘토</FormLabel>
          <SelectField
            value={selectedMentorId || ''}
            onChange={(e) => setSelectedMentorId(e.target.value ? Number(e.target.value) : null)}
            disabled={loadingMentors}
          >
            <option value="">선택해주세요.</option>
            {mentors.map((mentor) => (
              <option key={mentor.id} value={mentor.id}>
                {mentor.name} ({mentor.department})
              </option>
            ))}
          </SelectField>
          {loadingMentors && <HelperText>멘토 목록을 불러오는 중입니다.</HelperText>}
        </FormGroup>

        <FormGroup>
          <FormLabel>답변 내용</FormLabel>
          <EditorFrame>
            <RichTextEditor
              value={replyContent}
              onChange={setReplyContent}
              placeholder="내용을 입력해주세요."
              minHeight="300px"
              attachmentsEditable
            />
          </EditorFrame>
        </FormGroup>
      </SidePanelBody>

      <ReplyFooter>
        <PrimaryButton type="button" onClick={onSubmit} disabled={!replyContent.trim() || !selectedMentorId || isSubmitting}>
          {isSubmitting ? '등록 중...' : '등록하기'}
        </PrimaryButton>
      </ReplyFooter>
    </SidePanelModal>
  </SidePanelOverlay>
);

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ReplyFooter = styled.div`
  padding: 16px 0 0;
  display: flex;
  justify-content: flex-end;
`;

const InfoTopRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InfoLabel = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-weight: 500;
`;

const InfoText = styled.div`
  color: #171719;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.5;
  word-break: break-word;
`;

const ContentView = styled.div`
  max-height: 200px;
  padding: 12px 14px;
  overflow-y: auto;
  border-radius: 10px;
  border: 1px solid rgba(112, 115, 124, 0.16);
  background: rgba(112, 115, 124, 0.05);
  color: #171719;
  line-height: 1.6;
  word-break: break-word;

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
  }
`;

const EditorFrame = styled.div`
  border: 1px solid rgba(112, 115, 124, 0.16);
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
`;

export default CoffeeChatQuestionManagement;
